import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Plus, Search, Loader2, CheckCircle2,
    Thermometer, Bed, DollarSign, Calculator, Clock, Check,
    X, Heart, Activity, FileText, Zap, User,
    Pill, Droplets, Stethoscope, Utensils, Sparkles, ShoppingCart
} from 'lucide-react';

const PERIODIC_TYPES = ['medication', 'iv_fluid', 'vitals'] as const;
type TreatmentType = 'medication' | 'iv_fluid' | 'vitals' | 'diet' | 'cleaning' | 'procedure' | 'observation';

const TASK_META: Record<TreatmentType, { label: string; icon: React.ElementType; periodic: boolean; color: string }> = {
    medication:  { label: 'Medicamento',          icon: Pill,        periodic: true,  color: 'bg-violet-100 text-violet-700' },
    iv_fluid:    { label: 'Fluidoterapia / Suero', icon: Droplets,    periodic: true,  color: 'bg-sky-100 text-sky-700' },
    vitals:      { label: 'Signos Vitales',        icon: Activity,    periodic: true,  color: 'bg-rose-100 text-rose-700' },
    diet:        { label: 'Plan Alimentación',     icon: Utensils,    periodic: false, color: 'bg-orange-100 text-orange-700' },
    cleaning:    { label: 'Higiene / Aseo',        icon: Sparkles,    periodic: false, color: 'bg-emerald-100 text-emerald-700' },
    procedure:   { label: 'Procedimiento',         icon: Stethoscope, periodic: false, color: 'bg-amber-100 text-amber-700' },
    observation: { label: 'Evaluación Clínica',    icon: FileText,    periodic: false, color: 'bg-slate-100 text-slate-700' },
};
import { api } from '../../../services/api';
import { Hospitalization, HospitalizationTreatment, HospitalizationLog } from '../../../types';

// Convierte cualquier Date a un objeto donde getUTCHours() = hora en Santiago.
// Usa Date.UTC para que la aritmética (.getTime() + ms) sea siempre consistente.
const toChile = (d: Date): Date => {
    const p = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Santiago',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }).formatToParts(d);
    const g = (t: string) => parseInt(p.find(x => x.type === t)?.value ?? '0');
    return new Date(Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute'), g('second')));
};

// MySQL devuelve timestamps sin zona horaria ("2024-01-15 17:01:00").
// Sin la Z JS los parsea como hora local y genera desfase. Los forzamos a UTC.
const parseDB = (s: string): Date => new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');

interface LiveItem {
    treatment: any;
    hosp: any; // Hospitalization this item belongs to
    scheduledTime: Date;
    status: 'overdue' | 'warning' | 'upcoming';
    minsAgo?: number;
    minsLeft?: number;
}

interface StaffMember {
    id: string | number;
    name: string;
}

interface HospitalizationPanelProps {
    vetId: string | number;
    linkedPatients: any[];
    onSwitchTab: (tab: 'overview' | 'calendar' | 'patients' | 'records' | 'settings' | 'communications' | 'pos' | 'hospitalization') => void;
    initialAdmitPetId?: string | number | null;
    onClearInitialAdmit?: () => void;
    staffMembers?: StaffMember[];
}

export const HospitalizationPanel: React.FC<HospitalizationPanelProps> = ({
    vetId,
    linkedPatients,
    onSwitchTab,
    initialAdmitPetId,
    onClearInitialAdmit,
    staffMembers
}) => {
    const [hospitalizations, setHospitalizations] = useState<Hospitalization[]>([]);
    const [loading, setLoading] = useState(true);
    const [mainTab, setMainTab] = useState<'live' | 'current' | 'history'>('live');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedHosp, setSelectedHosp] = useState<Hospitalization | null>(null);
    const [detailTab, setDetailTab] = useState<'treatments' | 'logs' | 'billing' | 'calculators'>('treatments');

    // Modals
    const [showAdmitModal, setShowAdmitModal] = useState(false);
    const [admitting, setAdmitting] = useState(false);
    const [hospForm, setHospForm] = useState({
        pet_id: '',
        cage_label: '',
        gravity: 'low' as 'low' | 'medium' | 'critical',
        reason: '',
        initial_notes: ''
    });

    // Planned Treatment Form
    const [showAddTreatment, setShowAddTreatment] = useState(false);
    const [treatmentForm, setTreatmentForm] = useState({
        treatment_type: 'medication' as TreatmentType,
        name: '',
        frequency_hours: 8,
        start_time: '',
        doses_count: 0,
        price: 0,
        pos_product_id: '' as string | number
    });
    const [addingTreatment, setAddingTreatment] = useState(false);

    // Medication search
    const [medResults, setMedResults] = useState<any[]>([]);
    const [medSearchLoading, setMedSearchLoading] = useState(false);
    const [medDropOpen, setMedDropOpen] = useState(false);
    const medSearchRef = useRef<HTMLDivElement>(null);
    const swipeTouchStartX = useRef<number>(0);

    // Close med dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (medSearchRef.current && !medSearchRef.current.contains(e.target as Node)) {
                setMedDropOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Logging Vitals/Action
    const [showLogModal, setShowLogModal] = useState(false);
    const [selectedTreatment, setSelectedTreatment] = useState<HospitalizationTreatment | null>(null);
    const [logForm, setLogForm] = useState({
        action_type: 'treatment_done' as 'treatment_done' | 'vitals_check' | 'note',
        details: '',
        vitals: { temp: '', fc: '', fr: '', comment: '' },
        staff_name: 'Veterinario'
    });
    const [logging, setLogging] = useState(false);

    // Discharge Modal
    const [showDischargeModal, setShowDischargeModal] = useState(false);
    const [dischargeNotes, setDischargeNotes] = useState('');
    const [discharging, setDischarging] = useState(false);

    // Gravity change
    const [updatingGravity, setUpdatingGravity] = useState(false);

    // En Vivo tab
    const [liveTime, setLiveTime] = useState<Date>(() => new Date());
    const [liveRegisterItem, setLiveRegisterItem] = useState<LiveItem | null>(null);
    const [liveStaffPick, setLiveStaffPick] = useState('');
    const [liveNotes, setLiveNotes] = useState('Administrado');
    const [liveVitals, setLiveVitals] = useState({ temp: '', fc: '', fr: '', comment: '' });
    const [liveRegistering, setLiveRegistering] = useState(false);
    const liveNotifiedRef = useRef<Set<string>>(new Set());

    // Edición de intervalo de signos vitales
    const [editIntervalId, setEditIntervalId] = useState<number | null>(null);
    const [editIntervalHours, setEditIntervalHours] = useState(1);
    const [updatingInterval, setUpdatingInterval] = useState(false);

    // Staff list (from prop or fallback to single vet)
    const staff: StaffMember[] = (staffMembers && staffMembers.length > 0)
        ? staffMembers
        : [{ id: String(vetId), name: 'Veterinario a Cargo' }];

    // Helpers for live countdown display
    const fmtCountdown = (mins: number) => {
        const s = Math.max(0, Math.floor(mins * 60));
        const h = Math.floor(s / 3600);
        const m = Math.floor((s % 3600) / 60);
        const sec = s % 60;
        if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
        return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    };
    const fmtOverdue = (mins: number) => {
        const m = Math.floor(mins);
        if (m >= 60) return `${Math.floor(m / 60)}h ${(m % 60).toString().padStart(2, '0')}m`;
        return `${m} min`;
    };

    // CRI Calculator State
    const [cri, setCri] = useState({
        weight: '',
        fluidRate: '', // ml/kg/hr
        bagVolume: '500', // ml
        dose: '', // mg/kg/day or mg/kg/hr
        doseUnit: 'hr', // 'hr' or 'day'
        concentration: '' // mg/ml
    });

    const loadHospitalizations = async () => {
        if (!vetId || String(vetId) === 'undefined') return;
        setLoading(true);
        try {
            const isHistory = mainTab === 'history';
            const res = await api.getHospitalizations(vetId, isHistory);
            if (res.success) {
                setHospitalizations(res.hospitalizations || []);
            }
        } catch (e) {
            console.error("Error loading hospitalizations:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadHospitalizations();
    }, [mainTab, vetId]);

    useEffect(() => {
        if (initialAdmitPetId) {
            setHospForm(prev => ({ 
                ...prev, 
                pet_id: String(initialAdmitPetId) 
            }));
            setShowAdmitModal(true);
            if (onClearInitialAdmit) {
                onClearInitialAdmit();
            }
        }
    }, [initialAdmitPetId]);

    // Live clock — tick every second
    useEffect(() => {
        const tick = setInterval(() => setLiveTime(new Date()), 1000);
        return () => clearInterval(tick);
    }, []);

    // Clear live notifications when switching patient
    useEffect(() => {
        liveNotifiedRef.current.clear();
    }, [selectedHosp?.id]);

    // Handle Admit Patient
    const handleAdmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!hospForm.pet_id || !hospForm.cage_label || !hospForm.reason) {
            alert('Por favor completa los campos requeridos');
            return;
        }
        setAdmitting(true);
        try {
            const res = await api.admitPatient({
                vet_id: vetId,
                ...hospForm
            });
            if (res.success) {
                setShowAdmitModal(false);
                setHospForm({ pet_id: '', cage_label: '', gravity: 'low', reason: '', initial_notes: '' });
                loadHospitalizations();
            } else {
                alert(res.error || 'Error al hospitalizar paciente');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setAdmitting(false);
        }
    };

    const resetTreatmentForm = () => {
        setTreatmentForm({ treatment_type: 'medication', name: '', frequency_hours: 8, start_time: '', doses_count: 0, price: 0, pos_product_id: '' });
        setMedResults([]);
        setMedDropOpen(false);
    };

    // Medication search handler — busca desde 1 carácter en localStorage primero, luego backend
    const handleMedSearch = async (query: string) => {
        if (!query.trim()) { setMedResults([]); setMedDropOpen(false); return; }
        setMedDropOpen(true);
        setMedSearchLoading(true);
        try {
            const res = await api.getPOSProducts(vetId, undefined, query);
            if (res.success) setMedResults(res.products || []);
        } catch (_) { /* silencioso */ }
        finally { setMedSearchLoading(false); }
    };

    const selectMedProduct = (p: any) => {
        setTreatmentForm((prev: typeof treatmentForm) => ({ ...prev, name: p.name, price: p.price, pos_product_id: p.id }));
        setMedDropOpen(false);
    };

    // Handle Add planned treatment
    const handleAddTreatment = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedHosp || !treatmentForm.name) return;
        setAddingTreatment(true);
        const isPeriodic = (PERIODIC_TYPES as readonly string[]).includes(treatmentForm.treatment_type);
        try {
            const res = await api.addHospitalizationTreatment({
                hospitalization_id: selectedHosp.id,
                treatment_type: treatmentForm.treatment_type,
                name: treatmentForm.name,
                frequency_hours: isPeriodic ? treatmentForm.frequency_hours : 0,
                start_time: isPeriodic ? treatmentForm.start_time : '',
                doses_count: isPeriodic ? treatmentForm.doses_count : 0,
                price: treatmentForm.price,
                pos_product_id: treatmentForm.pos_product_id || null
            });
            if (res.success) {
                setShowAddTreatment(false);
                resetTreatmentForm();
                const updateRes = await api.getHospitalizations(vetId, mainTab === 'history');
                if (updateRes.success) {
                    const current = updateRes.hospitalizations.find((h: any) => h.id === selectedHosp.id);
                    if (current) setSelectedHosp(current);
                    setHospitalizations(updateRes.hospitalizations);
                }
            } else {
                alert(res.error || 'Error al agregar tratamiento');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setAddingTreatment(false);
        }
    };

    // Handle Log action (checking off task or vitals)
    const handleLogAction = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedHosp) return;
        setLogging(true);

        let finalDetails = logForm.details;
        if (logForm.action_type === 'vitals_check') {
            finalDetails = `T°: ${logForm.vitals.temp || 'N/A'}°C, FC: ${logForm.vitals.fc || 'N/A'} lpm, FR: ${logForm.vitals.fr || 'N/A'} rpm. Obs: ${logForm.vitals.comment || 'Ninguna'}`;
        }

        try {
            const res = await api.logHospitalizationAction({
                hospitalization_id: selectedHosp.id,
                treatment_id: selectedTreatment ? selectedTreatment.id : null,
                action_type: logForm.action_type,
                details: finalDetails,
                staff_name: logForm.staff_name
            });

            if (res.success) {
                // Reduce POS stock if this is a medication with a linked product
                if (selectedTreatment && (selectedTreatment as any).pos_product_id && (selectedTreatment as any).treatment_type === 'medication') {
                    const stockRes = await api.reducePOSProductStock((selectedTreatment as any).pos_product_id, 1);
                    if (stockRes.low_stock) {
                        // Browser push notification for low stock
                        const notify = () => new Notification('⚠ Stock Bajo — Inventario POS', {
                            body: `"${stockRes.product_name}" tiene solo ${stockRes.current_stock} unidades (alerta: ≤${stockRes.threshold})`,
                            icon: '/favicon.ico'
                        });
                        if (Notification.permission === 'granted') {
                            notify();
                        } else if (Notification.permission !== 'denied') {
                            Notification.requestPermission().then(p => { if (p === 'granted') notify(); });
                        }
                    }
                }

                setShowLogModal(false);
                setLogForm({ action_type: 'treatment_done', details: '', vitals: { temp: '', fc: '', fr: '', comment: '' }, staff_name: 'Veterinario' });
                setSelectedTreatment(null);

                // Refresh
                const updateRes = await api.getHospitalizations(vetId, mainTab === 'history');
                if (updateRes.success) {
                    const current = updateRes.hospitalizations.find((h: any) => h.id === selectedHosp.id);
                    if (current) setSelectedHosp(current);
                    setHospitalizations(updateRes.hospitalizations);
                }
            } else {
                alert(res.error || 'Error al registrar actividad');
            }
        } catch (err) {
            alert('Error de conexión');
        } finally {
            setLogging(false);
        }
    };

    // Handle Discharge — via modal
    const handleDischarge = async () => {
        if (!selectedHosp) return;
        setDischarging(true);
        try {
            const res = await api.dischargePatient(selectedHosp.id, dischargeNotes);
            if (res.success) {
                setShowDischargeModal(false);
                setDischargeNotes('');
                setSelectedHosp(null);
                loadHospitalizations();
            } else {
                alert(res.error || 'Error al procesar alta');
            }
        } catch (err) {
            alert('Error al conectar');
        } finally {
            setDischarging(false);
        }
    };

    // Handle Gravity Update
    const handleUpdateGravity = async (newGravity: 'low' | 'medium' | 'critical') => {
        if (!selectedHosp || selectedHosp.gravity === newGravity) return;
        setUpdatingGravity(true);
        try {
            const res = await api.updateHospitalizationGravity(selectedHosp.id, newGravity);
            if (res.success) {
                const updated = { ...selectedHosp, gravity: newGravity };
                setSelectedHosp(updated);
                setHospitalizations(prev => prev.map(h => h.id === selectedHosp.id ? updated : h));
            }
        } catch (err) { /* silencioso */ }
        finally { setUpdatingGravity(false); }
    };

    // Register live task execution from En Vivo tab
    const handleUpdateInterval = async (treatmentId: number, newHours: number) => {
        if (!selectedHosp || newHours < 1) return;
        setUpdatingInterval(true);
        try {
            const res = await api.updateHospitalizationTreatment({ treatment_id: treatmentId, frequency_hours: newHours });
            if (res.success) {
                setEditIntervalId(null);
                const updateRes = await api.getHospitalizations(vetId, false);
                if (updateRes.success) {
                    setHospitalizations(updateRes.hospitalizations);
                    const current = updateRes.hospitalizations.find((h: any) => h.id === selectedHosp.id);
                    if (current) setSelectedHosp(current);
                }
            }
        } catch (_) {}
        finally { setUpdatingInterval(false); }
    };

    const handleLiveRegister = async () => {
        if (!liveRegisterItem) return;
        const hosp = liveRegisterItem.hosp;
        if (!hosp) return;
        setLiveRegistering(true);
        const staffName = liveStaffPick || staff[0].name;
        const isVitals = liveRegisterItem.treatment.treatment_type === 'vitals';
        const details = isVitals
            ? `T°: ${liveVitals.temp || '—'}, FC: ${liveVitals.fc || '—'}, FR: ${liveVitals.fr || '—'}${liveVitals.comment ? ` | ${liveVitals.comment}` : ''}`
            : liveNotes || 'Administrado';
        try {
            const res = await api.logHospitalizationAction({
                hospitalization_id: hosp.id,
                treatment_id: liveRegisterItem.treatment.id,
                action_type: isVitals ? 'vitals_check' : 'treatment_done',
                details,
                staff_name: staffName
            });
            if (res.success) {
                if (liveRegisterItem.treatment.pos_product_id && liveRegisterItem.treatment.treatment_type === 'medication') {
                    const stockRes = await api.reducePOSProductStock(liveRegisterItem.treatment.pos_product_id, 1);
                    if (stockRes.low_stock) {
                        const n = () => new Notification('⚠ Stock Bajo', {
                            body: `"${stockRes.product_name}" tiene solo ${stockRes.current_stock} unidades`,
                            icon: '/favicon.ico'
                        });
                        if (Notification.permission === 'granted') n();
                        else if (Notification.permission !== 'denied') Notification.requestPermission().then(p => { if (p === 'granted') n(); });
                    }
                }
                setLiveRegisterItem(null);
                setLiveStaffPick('');
                setLiveNotes('Administrado');
                const updateRes = await api.getHospitalizations(vetId, false);
                if (updateRes.success) {
                    setHospitalizations(updateRes.hospitalizations);
                    if (selectedHosp) {
                        const current = updateRes.hospitalizations.find((h: any) => h.id === selectedHosp.id);
                        if (current) setSelectedHosp(current);
                    }
                }
            } else {
                alert(res.error || 'Error al registrar');
            }
        } catch (e) {
            console.error('handleLiveRegister error:', e);
            alert('Error al registrar la acción. Verifica la conexión.');
        } finally {
            setLiveRegistering(false);
        }
    };

    // Auto-billing Integration with POS Cart (via localStorage)
    const handleSendToPOS = async () => {
        if (!selectedHosp) return;
        // Find treatments with prices in the logs
        const billableItems = selectedHosp.logs.filter(l => {
            const t = selectedHosp.treatments.find(tr => tr.id === l.treatment_id);
            return t && parseFloat(String(t.price)) > 0 && l.billed === 0;
        });

        if (billableItems.length === 0) {
            alert('No hay consumos o medicamentos pendientes de facturar para este paciente.');
            return;
        }

        const itemsToCart = billableItems.map(l => {
            const t = selectedHosp.treatments.find(tr => tr.id === l.treatment_id)!;
            return {
                id: `HOSP-T${t.id}`,
                name: `[Hospitalización] ${t.name}`,
                price: parseFloat(String(t.price)),
                category: 'servicios',
                qty: 1,
                unit: 'servicio',
                sku: `HOSP-${t.id}`
            };
        });

        // 1. Guardar en localStorage para que VetPOS lo lea
        localStorage.setItem('vet_pos_pending_hosp_cart', JSON.stringify(itemsToCart));
        localStorage.setItem('vet_pos_pending_hosp_client', `${selectedHosp.pet_name} (${selectedHosp.owner_name || 'Sin dueño'})`);

        // 2. Marcar en la base de datos como facturados
        const logIds = billableItems.map(l => l.id);
        try {
            const res = await api.markHospitalizationBilled(logIds);
            if (res.success) {
                // Redirigir al POS
                onSwitchTab('pos');
            } else {
                alert('Los consumos se enviaron al carro, pero hubo un problema al marcarlos como facturados en la bitácora.');
            }
        } catch (e) {
            onSwitchTab('pos');
        }
    };

    // CRI Calculations
    const criCalculations = useMemo(() => {
        const w = parseFloat(cri.weight);
        const fr = parseFloat(cri.fluidRate);
        const bv = parseFloat(cri.bagVolume);
        const d = parseFloat(cri.dose);
        const conc = parseFloat(cri.concentration);

        if (!w || !fr || !bv) return null;

        // Fluid Rate calculations
        const mlPerHour = w * fr; // ml/hr
        const bagDurationHours = bv / mlPerHour; // hrs
        const dropsPerMin = (mlPerHour * 20) / 60; // Assumes standard 20 drops/ml iv set

        // Drug addition (CRI) calculations
        let drugVolumeToAdd = 0;
        let doseMgPerHour = 0;

        if (d && conc) {
            if (cri.doseUnit === 'day') {
                // dose in mg/kg/day
                doseMgPerHour = (d * w) / 24; // mg/hr
            } else {
                // dose in mg/kg/hr
                doseMgPerHour = d * w; // mg/hr
            }
            const totalMgNeededInBag = doseMgPerHour * bagDurationHours;
            drugVolumeToAdd = totalMgNeededInBag / conc; // ml
        }

        return {
            mlPerHour: Math.round(mlPerHour * 10) / 10,
            duration: Math.round(bagDurationHours * 10) / 10,
            dropsMin: Math.round(dropsPerMin),
            drugVolume: Math.round(drugVolumeToAdd * 100) / 100
        };
    }, [cri]);

    // En Vivo — ALL admitted patients' periodic tasks, updated every second
    const allLiveItems = useMemo<LiveItem[]>(() => {
        // Todo en hora Chile para que start_time "08:00" signifique las 08:00 en Santiago
        const now = toChile(liveTime);
        const items: LiveItem[] = [];
        const admitted = hospitalizations.filter(h => h.status === 'admitted');

        admitted.forEach(hosp => {
            (hosp.treatments as any[]).forEach((t: any) => {
                if (!(PERIODIC_TYPES as readonly string[]).includes(t.treatment_type)) return;
                if (!t.start_time || !t.frequency_hours) return;

                const dosesTotal = parseInt(String(t.doses_count)) || 0;
                const logsForT = (hosp.logs as any[]).filter((l: any) => l.treatment_id === t.id);
                if (dosesTotal > 0 && logsForT.length >= dosesTotal) return;

                const freqMs = parseInt(String(t.frequency_hours)) * 3600000;
                if (freqMs <= 0) return;
                const [startH, startM] = (t.start_time as string).split(':').map(Number);
                // Admission en coordenadas Chile-UTC (getUTCHours() = hora Santiago)
                const admission = toChile(parseDB(hosp.admission_date));

                // setUTCHours para operar en el mismo eje que toChile()
                let firstExec = new Date(admission);
                firstExec.setUTCHours(startH, startM, 0, 0);
                if (firstExec.getTime() < admission.getTime()) {
                    firstExec = new Date(firstExec.getTime() + freqMs);
                }

                let cursor = new Date(firstExec);
                let lastPastExec: Date | null = null;
                while (cursor.getTime() <= now.getTime()) {
                    lastPastExec = new Date(cursor);
                    cursor = new Date(cursor.getTime() + freqMs);
                }
                const nextExec = new Date(cursor);
                const minsLeft = (nextExec.getTime() - now.getTime()) / 60000;

                if (lastPastExec) {
                    // Verificar si hubo un registro ENTRE lastPastExec y nextExec (rango, no ventana fija)
                    const wasLogged = logsForT.some((l: any) => {
                        const logMs = toChile(parseDB(l.logged_at)).getTime();
                        return logMs >= lastPastExec!.getTime() && logMs < nextExec.getTime();
                    });
                    if (!wasLogged) {
                        items.push({ treatment: t, hosp, scheduledTime: lastPastExec, status: 'overdue', minsAgo: (now.getTime() - lastPastExec.getTime()) / 60000 });
                        return;
                    }
                }
                items.push({ treatment: t, hosp, scheduledTime: nextExec, status: minsLeft <= 5 ? 'warning' : 'upcoming', minsLeft });
            });
        });

        const rank = { overdue: 0, warning: 1, upcoming: 2 };
        items.sort((a, b) => {
            if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status];
            if (a.status === 'overdue') return (b.minsAgo || 0) - (a.minsAgo || 0);
            return (a.minsLeft || 0) - (b.minsLeft || 0);
        });
        return items;
    }, [hospitalizations, liveTime]);

    // Push notifications for live status transitions (all patients)
    useEffect(() => {
        allLiveItems.forEach((item: LiveItem) => {
            const key = `${item.hosp.id}-${item.treatment.id}-${item.status}`;
            if (liveNotifiedRef.current.has(key)) return;
            liveNotifiedRef.current.add(key);
            const fire = (title: string, body: string) => {
                const n = () => new Notification(title, { body, icon: '/favicon.ico' });
                if (Notification.permission === 'granted') n();
                else if (Notification.permission !== 'denied') Notification.requestPermission().then(p => { if (p === 'granted') n(); });
            };
            if (item.status === 'warning') fire('⏰ Tarea próxima — 5 min', `${item.treatment.name} · ${item.hosp.pet_name}`);
            if (item.status === 'overdue') fire('🚨 Tarea vencida', `${item.treatment.name} · ${item.hosp.pet_name} — sin registrar`);
        });
    }, [allLiveItems]);

    // Search and filter patients
    const filteredHosp = hospitalizations.filter(h => {
        const matchesQuery = h.pet_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.cage_label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            h.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (h.owner_name && h.owner_name.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesQuery;
    });

    const gravityLabel = (g: string) => {
        switch (g) {
            case 'critical': return 'CRÍTICO';
            case 'medium': return 'ESTABLE S DELICADO';
            default: return 'ESTABLE';
        }
    };

    const gravityColor = (g: string) => {
        switch (g) {
            case 'critical': return 'bg-rose-500 border-rose-600 text-white';
            case 'medium': return 'bg-amber-500 border-amber-600 text-white';
            default: return 'bg-emerald-500 border-emerald-600 text-white';
        }
    };

    return (
        <div className="h-full flex flex-col overflow-hidden bg-slate-50 relative font-sans">

            {/* ── DESKTOP HEADER ── */}
            <div className="hidden lg:flex items-center justify-between px-6 py-3.5 bg-white border-b border-slate-100 shrink-0 gap-4">
                <div className="shrink-0">
                    <h3 className="text-lg font-black text-slate-800 flex items-center gap-2 uppercase tracking-tight italic">
                        <Bed size={20} className="text-indigo-600" /> Hospitalizados
                    </h3>
                </div>
                <div className="flex bg-slate-100 rounded-2xl p-1 shadow-inner">
                    <button onClick={() => setMainTab('live')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wide transition-all relative ${mainTab === 'live' ? 'bg-white shadow text-emerald-600' : 'text-slate-400 hover:text-slate-700'}`}>
                        <Zap size={14} /> En Vivo
                        {allLiveItems.some((i: LiveItem) => i.status === 'overdue') && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-rose-500 rounded-full" />}
                        {!allLiveItems.some((i: LiveItem) => i.status === 'overdue') && allLiveItems.some((i: LiveItem) => i.status === 'warning') && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-amber-400 rounded-full animate-pulse" />}
                    </button>
                    <button onClick={() => setMainTab('current')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wide transition-all ${mainTab === 'current' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-700'}`}>
                        Internados ({hospitalizations.filter(h => h.status === 'admitted').length})
                    </button>
                    <button onClick={() => setMainTab('history')} className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wide transition-all ${mainTab === 'history' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-700'}`}>
                        Historial / Altas
                    </button>
                </div>
                <div className="flex items-center gap-3">
                    {mainTab !== 'live' && (
                        <div className="relative w-60">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-300" size={15} />
                            <input type="text" placeholder="Buscar jaula, mascota..." className="bg-slate-50 border border-slate-200 pl-10 pr-3 py-2.5 rounded-xl text-sm font-bold w-full outline-none focus:border-indigo-400" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                        </div>
                    )}
                    <button onClick={() => setShowAdmitModal(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-black uppercase text-xs tracking-wider shadow-md flex items-center gap-2 shrink-0">
                        <Plus size={14} /> Hospitalizar
                    </button>
                </div>
            </div>

            {/* ── MÓVIL: tabs compactos en 1 fila ── */}
            <div className="lg:hidden bg-white border-b border-slate-100 shrink-0">
                <div className="flex items-center p-1.5 gap-1">
                    <button
                        onClick={() => setMainTab('live')}
                        className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wide transition-all relative ${mainTab === 'live' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500'}`}
                    >
                        <Zap size={11} /> En Vivo
                        {allLiveItems.some((i: LiveItem) => i.status === 'overdue') && <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full" />}
                        {!allLiveItems.some((i: LiveItem) => i.status === 'overdue') && allLiveItems.some((i: LiveItem) => i.status === 'warning') && <span className="absolute top-1 right-1 w-2 h-2 bg-amber-400 rounded-full animate-pulse" />}
                    </button>
                    <button
                        onClick={() => setMainTab('current')}
                        className={`flex-1 flex items-center justify-center py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wide transition-all ${mainTab === 'current' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500'}`}
                    >
                        Internados ({hospitalizations.filter(h => h.status === 'admitted').length})
                    </button>
                    <button
                        onClick={() => setMainTab('history')}
                        className={`flex-1 flex items-center justify-center py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wide transition-all ${mainTab === 'history' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500'}`}
                    >
                        Historial
                    </button>
                    <button
                        onClick={() => setShowAdmitModal(true)}
                        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl bg-indigo-600 text-white shadow-sm ml-1"
                    >
                        <Plus size={15} />
                    </button>
                </div>
                {mainTab !== 'live' && (
                    <div className="px-3 pb-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={13} />
                            <input type="text" placeholder="Buscar mascota, jaula..." className="bg-slate-50 border border-slate-100 pl-8 pr-3 py-2 rounded-xl text-xs font-bold w-full outline-none focus:border-indigo-300" value={searchTerm} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)} />
                        </div>
                    </div>
                )}
            </div>

            {/* ── EN VIVO VIEW ── todas las tareas de todos los pacientes */}
            {mainTab === 'live' && (
                <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4 lg:space-y-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
                                Monitoreo en Tiempo Real
                            </h4>
                            <p className="text-[10px] text-slate-400 font-bold tabular-nums mt-0.5">
                                {liveTime.toLocaleTimeString('es-CL', { timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                &nbsp;·&nbsp;{hospitalizations.filter(h => h.status === 'admitted').length} paciente{hospitalizations.filter(h => h.status === 'admitted').length !== 1 ? 's' : ''} internado{hospitalizations.filter(h => h.status === 'admitted').length !== 1 ? 's' : ''}
                            </p>
                        </div>
                        {(allLiveItems.some((i: LiveItem) => i.status !== 'upcoming')) && (
                            <div className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase ${allLiveItems.some((i: LiveItem) => i.status === 'overdue') ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}`}>
                                {allLiveItems.filter((i: LiveItem) => i.status === 'overdue').length > 0 && `${allLiveItems.filter((i: LiveItem) => i.status === 'overdue').length} vencida${allLiveItems.filter((i: LiveItem) => i.status === 'overdue').length !== 1 ? 's' : ''}`}
                                {allLiveItems.filter((i: LiveItem) => i.status === 'overdue').length > 0 && allLiveItems.filter((i: LiveItem) => i.status === 'warning').length > 0 && ' · '}
                                {allLiveItems.filter((i: LiveItem) => i.status === 'warning').length > 0 && `${allLiveItems.filter((i: LiveItem) => i.status === 'warning').length} próxima${allLiveItems.filter((i: LiveItem) => i.status === 'warning').length !== 1 ? 's' : ''}`}
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                            <Loader2 className="animate-spin mb-3 text-indigo-400" size={28} />
                            <p className="text-[10px] font-black uppercase tracking-widest">Cargando...</p>
                        </div>
                    ) : allLiveItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400 text-center">
                            <Zap size={40} className="mb-3 opacity-20" />
                            <p className="text-sm font-black uppercase italic">Sin tareas periódicas activas</p>
                            <p className="text-[11px] font-bold mt-2 max-w-xs leading-relaxed">
                                {hospitalizations.filter(h => h.status === 'admitted').length === 0
                                    ? 'No hay pacientes hospitalizados actualmente. Presiona "+ Hospitalizar".'
                                    : 'Los pacientes internados no tienen tareas periódicas. Ve a Internados → selecciona paciente → Tareas → Agregar Tarea.'}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                            {allLiveItems.map((item: LiveItem, idx: number) => {
                                const meta = TASK_META[item.treatment.treatment_type as TreatmentType] || TASK_META.observation;
                                const isOverdue = item.status === 'overdue';
                                const isWarning = item.status === 'warning';
                                const isSelected = liveRegisterItem?.treatment.id === item.treatment.id && liveRegisterItem?.hosp?.id === item.hosp.id;
                                return (
                                    <div key={`${item.hosp.id}-${item.treatment.id}-${idx}`} className={`rounded-2xl border-2 overflow-hidden transition-all ${isOverdue ? 'border-rose-300 bg-rose-50/40' : isWarning ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white'}`}>
                                        {/* Barra superior de estado */}
                                        <div className={`h-1.5 ${isOverdue ? 'bg-rose-500' : isWarning ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`} />

                                        {/* Layout: vertical móvil, horizontal desktop */}
                                        <div className="flex flex-col lg:flex-row">
                                            {/* ── Foto mascota ── */}
                                            <div className="w-full h-28 lg:w-40 lg:h-auto lg:self-stretch shrink-0 relative overflow-hidden">
                                                {item.hosp.pet_image_url ? (
                                                    <img
                                                        src={item.hosp.pet_image_url}
                                                        alt={item.hosp.pet_name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-slate-100">
                                                        <span className="text-5xl lg:text-6xl select-none">
                                                            {item.hosp.pet_species?.toLowerCase().includes('gat') ? '🐱' : item.hosp.pet_species?.toLowerCase().includes('perr') ? '🐶' : '🐾'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* ── Columna derecha ── */}
                                            <div className="flex-1 min-w-0 flex flex-col p-3 lg:p-4 gap-2 lg:gap-3">

                                                {/* Nombre + jaula + especie en una fila */}
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-xl lg:text-4xl font-black text-slate-900 uppercase italic leading-none truncate flex-1">{item.hosp.pet_name}</p>
                                                    <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
                                                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border uppercase tracking-wide ${item.hosp.gravity === 'critical' ? 'bg-rose-100 text-rose-700 border-rose-200' : item.hosp.gravity === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-rose-100 text-rose-500 border-rose-200'}`}>
                                                            {item.hosp.cage_label}
                                                        </span>
                                                        <span className="hidden lg:inline text-sm text-slate-500 font-semibold">{item.hosp.pet_species}</span>
                                                    </div>
                                                </div>

                                                {/* Tarea + timer */}
                                                <div className="flex items-start gap-2">
                                                    <div className={`w-7 h-7 lg:w-9 lg:h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${meta.color}`}>
                                                        <meta.icon size={14} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-black text-slate-800 text-xs lg:text-sm uppercase leading-tight truncate">{item.treatment.name}</p>
                                                        <p className="text-[10px] lg:text-xs font-bold text-slate-400 mt-0.5">{meta.label} · cada {item.treatment.frequency_hours}h</p>
                                                        <p className="text-[10px] lg:text-xs font-bold text-slate-400 flex items-center gap-1 mt-0.5">
                                                            <Clock size={9} />
                                                            {isOverdue ? 'Debió ser a las' : 'Próxima:'}&nbsp;
                                                            <span className={`font-black ${isOverdue ? 'text-rose-600' : 'text-slate-600'}`}>
                                                                {item.scheduledTime.toLocaleTimeString('es-CL', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </p>
                                                        {(() => {
                                                            const dosesTotal = parseInt(String(item.treatment.doses_count)) || 0;
                                                            const execCount = (item.hosp.logs as any[]).filter((l: any) => l.treatment_id === item.treatment.id).length;
                                                            if (dosesTotal <= 0 || item.treatment.treatment_type === 'vitals') return null;
                                                            const left = Math.max(0, dosesTotal - execCount);
                                                            return (
                                                                <div className="mt-1.5 space-y-1">
                                                                    <div className="flex items-center justify-between text-[10px] font-black uppercase">
                                                                        <span className={left === 0 ? 'text-emerald-600' : 'text-slate-500'}>{execCount}/{dosesTotal} dosis</span>
                                                                        <span className={left === 0 ? 'text-emerald-600' : 'text-amber-600'}>{left > 0 ? `${left} restante${left !== 1 ? 's' : ''}` : '¡Completado!'}</span>
                                                                    </div>
                                                                    <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                                        <div className={`h-full rounded-full transition-all duration-500 ${left === 0 ? 'bg-emerald-500' : 'bg-indigo-500'}`} style={{ width: `${Math.min(100, (execCount / dosesTotal) * 100)}%` }} />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                    {/* Contador */}
                                                    <div className={`text-right shrink-0 ${isOverdue ? 'text-rose-600' : isWarning ? 'text-amber-500' : 'text-slate-500'}`}>
                                                        <p className="text-[8px] lg:text-[9px] font-black uppercase leading-none mb-1 tracking-widest">{isOverdue ? 'Vencida' : isWarning ? '¡Ahora!' : 'Próxima'}</p>
                                                        <p className="text-xl lg:text-3xl font-black tabular-nums leading-none">{isOverdue ? fmtOverdue(item.minsAgo || 0) : fmtCountdown(item.minsLeft || 0)}</p>
                                                    </div>
                                                </div>

                                                {/* Botón registrar */}
                                                <button
                                                    onClick={() => { if (isSelected) { setLiveRegisterItem(null); } else { setLiveRegisterItem(item); setLiveStaffPick(staff.length === 1 ? staff[0].name : ''); setLiveNotes('Administrado'); setLiveVitals({ temp: '', fc: '', fr: '', comment: '' }); } }}
                                                    className={`w-full py-2.5 lg:py-3 rounded-xl font-black uppercase text-xs lg:text-sm tracking-wider transition-all flex items-center justify-center gap-2 mt-auto ${isSelected ? 'bg-slate-200 text-slate-500' : isOverdue ? 'bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-200' : isWarning ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-md shadow-amber-200' : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'}`}
                                                >
                                                    <CheckCircle2 size={13} />{isSelected ? 'Cancelar' : 'Registrar Ejecución'}
                                                </button>

                                                {/* Staff picker inline */}
                                                {isSelected && (
                                                    <div className="pt-3 border-t border-slate-200 space-y-3 animate-in slide-in-from-top-2">
                                                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><User size={11} /> ¿Quién lo realizó?</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {staff.map((member: StaffMember) => (
                                                                <button key={String(member.id)} type="button" onClick={() => setLiveStaffPick(member.name)} className={`px-3 py-2 rounded-xl text-xs font-black uppercase transition-all border-2 ${liveStaffPick === member.name ? 'bg-indigo-600 border-indigo-500 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-indigo-300'}`}>{member.name}</button>
                                                            ))}
                                                        </div>
                                                        {item.treatment.treatment_type === 'vitals' ? (
                                                            <div className="space-y-2">
                                                                <div className="grid grid-cols-3 gap-2">
                                                                    <div>
                                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block mb-1">T° (°C)</label>
                                                                        <input type="text" inputMode="decimal" placeholder="38.5" value={liveVitals.temp} onChange={e => setLiveVitals(v => ({ ...v, temp: e.target.value }))} className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2 text-sm font-bold text-center outline-none" />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block mb-1">FC (lpm)</label>
                                                                        <input type="text" inputMode="numeric" placeholder="120" value={liveVitals.fc} onChange={e => setLiveVitals(v => ({ ...v, fc: e.target.value }))} className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2 text-sm font-bold text-center outline-none" />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block mb-1">FR (rpm)</label>
                                                                        <input type="text" inputMode="numeric" placeholder="30" value={liveVitals.fr} onChange={e => setLiveVitals(v => ({ ...v, fr: e.target.value }))} className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2 text-sm font-bold text-center outline-none" />
                                                                    </div>
                                                                </div>
                                                                <input type="text" placeholder="Observaciones adicionales..." value={liveVitals.comment} onChange={e => setLiveVitals(v => ({ ...v, comment: e.target.value }))} className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-3 py-2 text-sm font-bold outline-none" />
                                                            </div>
                                                        ) : (
                                                            <textarea value={liveNotes} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setLiveNotes(e.target.value)} placeholder="Observaciones..." className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-3 py-2 text-sm font-bold outline-none resize-none" rows={2} />
                                                        )}
                                                        <button onClick={handleLiveRegister} disabled={liveRegistering || !liveStaffPick} className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black uppercase text-sm tracking-widest disabled:opacity-50 flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
                                                            {liveRegistering ? <><Loader2 size={14} className="animate-spin" /> Guardando...</> : <><Check size={14} /> Confirmar — {liveStaffPick || '...'}</>}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ── INTERNADOS / HISTORIAL VIEW ── */}
            {(mainTab === 'current' || mainTab === 'history') && (
            <div className="flex-1 flex overflow-hidden">
            {/* Patient grid — oculto en móvil cuando hay mascota seleccionada */}
            <div className={`flex-col overflow-hidden border-r border-slate-100 p-4 lg:p-6 ${selectedHosp ? 'hidden lg:flex lg:flex-1' : 'flex flex-1'}`}>
                {/* Grid */}
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-400 h-full">
                            <Loader2 className="animate-spin mb-4 text-indigo-500" size={32} />
                            <p className="font-black uppercase tracking-widest text-[9px]">Cargando pacientes...</p>
                        </div>
                    ) : filteredHosp.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-slate-300 italic text-sm h-full bg-white rounded-3xl border border-dashed border-slate-200">
                            <Bed size={48} className="mb-4 opacity-20" />
                            No hay pacientes hospitalizados registrados en esta sección.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                            {filteredHosp.map((h) => {
                                const lastVitalsLog = h.logs.find(l => l.action_type === 'vitals_check');
                                const pendingTasks = h.treatments.filter((t: any) => (PERIODIC_TYPES as readonly string[]).includes(t.treatment_type)).length;
                                const admissionDate = new Date(h.admission_date);
                                return (
                                    <div
                                        key={h.id}
                                        onClick={() => { setSelectedHosp(h); setDetailTab('treatments'); }}
                                        className={`bg-white rounded-3xl border-2 cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-xl overflow-hidden ${
                                            selectedHosp?.id === h.id ? 'border-indigo-500 shadow-lg ring-4 ring-indigo-500/10' : 'border-slate-100 hover:border-slate-200'
                                        }`}
                                    >
                                        {/* Barra de gravedad */}
                                        <div className={`h-2 ${h.gravity === 'critical' ? 'bg-rose-500' : h.gravity === 'medium' ? 'bg-amber-400' : 'bg-emerald-500'}`} />

                                        {/* Layout horizontal: foto | info */}
                                        <div className="flex">
                                            {/* ── Foto mascota ── */}
                                            <div className="w-36 shrink-0">
                                                {h.pet_image_url ? (
                                                    <img
                                                        src={h.pet_image_url}
                                                        alt={h.pet_name}
                                                        className="w-full h-full object-cover"
                                                        style={{ minHeight: 170 }}
                                                    />
                                                ) : (
                                                    <div className="w-full flex items-center justify-center bg-slate-100" style={{ minHeight: 170 }}>
                                                        <span className="text-6xl select-none">
                                                            {h.pet_species?.toLowerCase().includes('gat') ? '🐱' : h.pet_species?.toLowerCase().includes('perr') ? '🐶' : '🐾'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* ── Columna derecha ── */}
                                            <div className="flex-1 min-w-0 p-4 flex flex-col gap-2.5">
                                                {/* Jaula + gravedad */}
                                                <div className="flex items-center justify-between">
                                                    <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full uppercase tracking-wide">
                                                        {h.cage_label}
                                                    </span>
                                                    <span className={`text-xs font-black px-3 py-1 rounded-full uppercase ${
                                                        h.gravity === 'critical' ? 'bg-rose-100 text-rose-700' : h.gravity === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                                                    }`}>
                                                        {gravityLabel(h.gravity)}
                                                    </span>
                                                </div>

                                                {/* Nombre + especie + dueño */}
                                                <div className="min-w-0">
                                                    <h4 className="font-black text-slate-800 text-2xl uppercase italic leading-tight truncate">{h.pet_name}</h4>
                                                    <p className="text-xs font-bold text-slate-400 uppercase mt-0.5 truncate">{h.pet_species}{h.pet_breed ? ` · ${h.pet_breed}` : ''}</p>
                                                    <p className="text-xs font-bold text-slate-500 mt-1 flex items-center gap-1">
                                                        <User size={11} className="text-slate-400 shrink-0" /> {h.owner_name || 'Sin propietario'}
                                                    </p>
                                                </div>

                                                {/* Diagnóstico + ingreso */}
                                                <div className="flex items-start justify-between gap-2 border-t border-slate-100 pt-2.5">
                                                    <div className="min-w-0">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Diagnóstico</p>
                                                        <p className="text-sm font-bold text-slate-700 leading-snug truncate">{h.reason}</p>
                                                    </div>
                                                    <div className="text-right shrink-0">
                                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Ingreso</p>
                                                        <p className="text-xs font-bold text-slate-600 tabular-nums">
                                                            {admissionDate.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })}
                                                        </p>
                                                        <p className="text-xs font-bold text-slate-500 tabular-nums">
                                                            {admissionDate.toLocaleTimeString('es-CL', { timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    </div>
                                                </div>

                                                {/* Signos vitales + tareas */}
                                                <div className="flex items-center justify-between border-t border-slate-100 pt-2">
                                                    {lastVitalsLog ? (
                                                        <div className="flex items-center gap-3 text-sm">
                                                            <span className="flex items-center gap-1 text-indigo-600 font-bold">
                                                                <Thermometer size={13} /> {lastVitalsLog.details.split(',')[0]?.replace('T°:', '').trim() || '—'}°
                                                            </span>
                                                            <span className="flex items-center gap-1 text-rose-500 font-bold">
                                                                <Heart size={13} /> {lastVitalsLog.details.split(',')[1]?.replace('FC:', '').trim() || '—'}
                                                            </span>
                                                            <span className="flex items-center gap-1 text-sky-500 font-bold">
                                                                <Activity size={13} /> {lastVitalsLog.details.split(',')[2]?.replace('FR:', '').trim() || '—'}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs font-bold text-slate-400 italic">Sin signos vitales aún</p>
                                                    )}
                                                    {pendingTasks > 0 && (
                                                        <span className="text-xs font-black bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full shrink-0">
                                                            {pendingTasks} tarea{pendingTasks !== 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* RIGHT PANEL: PATIENT HOPI DETAIL VIEW */}
            {selectedHosp ? (
                <div
                    className="flex-1 lg:flex-[0_0_50%] lg:w-1/2 bg-white shadow-2xl flex flex-col overflow-hidden shrink-0 border-l border-slate-200 animate-in slide-in-from-right duration-300"
                    onTouchStart={(e: React.TouchEvent) => { swipeTouchStartX.current = e.touches[0].clientX; }}
                    onTouchEnd={(e: React.TouchEvent) => { if (e.changedTouches[0].clientX - swipeTouchStartX.current > 60) setSelectedHosp(null); }}
                >
                    {/* Header */}
                    <div className="bg-white border-b border-slate-100 text-slate-800 px-4 lg:px-6 pt-3 lg:pt-5 pb-0 shrink-0">

                        {/* Botón volver — solo móvil */}
                        <button
                            onClick={() => setSelectedHosp(null)}
                            className="lg:hidden flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-indigo-600 mb-3 -ml-1"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                            Todos los internados
                        </button>

                        {/* Fila 1: jaula + especie + botón cerrar + ingreso */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-black bg-indigo-600 text-white px-3 py-1.5 rounded-xl uppercase tracking-widest">
                                    {selectedHosp.cage_label}
                                </span>
                                <span className="text-sm text-slate-500 font-bold">
                                    {selectedHosp.pet_species} · {selectedHosp.pet_breed} · {selectedHosp.pet_gender}
                                </span>
                            </div>
                            <div className="flex items-start gap-3 shrink-0">
                                <div className="text-right">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Ingreso</p>
                                    <p className="text-xs font-bold text-slate-700 tabular-nums">
                                        {new Date(selectedHosp.admission_date).toLocaleDateString('es-CL', { day: 'numeric', month: 'numeric' })},&nbsp;
                                        {new Date(selectedHosp.admission_date).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                {selectedHosp.status === 'admitted' && (
                                    <button
                                        onClick={() => setShowDischargeModal(true)}
                                        className="px-3 py-1.5 bg-rose-500 hover:bg-rose-600 text-white rounded-xl font-black uppercase text-[9px] tracking-wider shrink-0 transition-all shadow-sm"
                                    >
                                        Alta Médica
                                    </button>
                                )}
                                <button
                                    onClick={() => setSelectedHosp(null)}
                                    className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-lg transition-colors shrink-0"
                                >
                                    <X size={15} />
                                </button>
                            </div>
                        </div>

                        {/* Fila 2: nombre grande */}
                        <h3 className="text-5xl font-black uppercase italic tracking-tight leading-none text-slate-900 mb-3">
                            {selectedHosp.pet_name}
                        </h3>

                        {/* Fila 3: dueño + diagnóstico */}
                        <div className="flex gap-8 mb-4">
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Dueño</p>
                                <p className="text-sm font-bold text-slate-800">{selectedHosp.owner_name || 'Sin registro'}</p>
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Diagnóstico</p>
                                <p className="text-sm font-bold text-slate-800">{selectedHosp.reason}</p>
                            </div>
                        </div>

                        {/* Fila 4: botones de estado — el activo es más grande y dominante */}
                        <div className="flex gap-2 mb-1">
                            {(['low', 'medium', 'critical'] as const).map(g => {
                                const isActive = selectedHosp.gravity === g;
                                return (
                                    <button
                                        key={g}
                                        onClick={() => handleUpdateGravity(g)}
                                        disabled={updatingGravity}
                                        className={`rounded-2xl font-black uppercase tracking-wide transition-all duration-300 disabled:opacity-60 ${
                                            isActive
                                                ? `flex-[2] py-3.5 text-sm shadow-lg ${
                                                    g === 'critical'
                                                        ? 'bg-rose-500 text-white shadow-rose-200'
                                                        : g === 'medium'
                                                            ? 'bg-amber-400 text-white shadow-amber-200'
                                                            : 'bg-emerald-500 text-white shadow-emerald-200'
                                                }`
                                                : 'flex-[1] py-2.5 text-[10px] bg-slate-100 text-slate-400 hover:bg-slate-200 hover:text-slate-600'
                                        }`}
                                    >
                                        {updatingGravity && isActive
                                            ? <Loader2 size={14} className="animate-spin mx-auto" />
                                            : gravityLabel(g)
                                        }
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Detail Tabs */}
                    <div className="flex border-b-2 border-slate-100 bg-white shrink-0">
                        {[
                            { id: 'treatments',  label: 'Tareas',   icon: FileText     },
                            { id: 'logs',        label: 'Bitácora', icon: Activity     },
                            { id: 'billing',     label: 'Cobros',   icon: ShoppingCart },
                            { id: 'calculators', label: 'CRI',      icon: Calculator   }
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setDetailTab(t.id as any)}
                                className={`flex-1 flex flex-col items-center py-4 gap-1.5 text-xs font-black uppercase tracking-wide transition-all border-b-2 -mb-0.5 ${
                                    detailTab === t.id
                                        ? 'border-indigo-600 text-indigo-600 bg-indigo-50/50'
                                        : 'border-transparent text-slate-400 hover:text-slate-600'
                                }`}
                            >
                                <t.icon size={14} />
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        
                        {/* TAB 1: FLOW SHEET (TREATMENTS) */}
                        {detailTab === 'treatments' && (
                            <div className="space-y-6">
                                {/* Planner list */}
                                <div className="flex justify-between items-center">
                                    <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest">Tareas Planificadas</h4>
                                    {selectedHosp.status === 'admitted' && (
                                        <button
                                            onClick={() => setShowAddTreatment(!showAddTreatment)}
                                            className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-[10px] font-black uppercase tracking-wide transition-all"
                                        >
                                            <Plus size={11} /> Agregar Tarea
                                        </button>
                                    )}
                                </div>

                                {/* Form Add Treatment */}
                                {showAddTreatment && (() => {
                                    const isPeriodic = (PERIODIC_TYPES as readonly string[]).includes(treatmentForm.treatment_type);
                                    const isMed = treatmentForm.treatment_type === 'medication';
                                    const isVitals = treatmentForm.treatment_type === 'vitals';
                                    const meta = TASK_META[treatmentForm.treatment_type];
                                    return (
                                    <form onSubmit={handleAddTreatment} className="bg-slate-50 border-2 border-indigo-100 rounded-2xl p-4 space-y-4 animate-in slide-in-from-top-2">

                                        {/* Task type selector */}
                                        <div>
                                            <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-2">Tipo de tarea</label>
                                            <div className="grid grid-cols-2 gap-1.5">
                                                {(Object.entries(TASK_META) as [TreatmentType, typeof TASK_META[TreatmentType]][]).map(([type, m]) => (
                                                    <button
                                                        key={type}
                                                        type="button"
                                                        onClick={() => {
                                                            setTreatmentForm(prev => ({ ...prev, treatment_type: type, name: type === 'vitals' ? 'Signos Vitales' : '', price: 0, pos_product_id: '', doses_count: 0 }));
                                                            setMedResults([]);
                                                        }}
                                                        className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-[10px] font-black uppercase transition-all ${
                                                            treatmentForm.treatment_type === type
                                                                ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                                                                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300'
                                                        }`}
                                                    >
                                                        <m.icon size={12} />
                                                        {m.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Badge: periodic vs service */}
                                        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[9px] font-black uppercase tracking-wider ${meta.color}`}>
                                            <meta.icon size={11} />
                                            {isPeriodic ? 'Tarea periódica — requiere programación de horario' : 'Servicio clínico — sin programación de horario'}
                                        </div>

                                        {/* Name field — oculto para vitales (nombre fijo), busca en POS para medicamento */}
                                        {!isVitals && <div ref={isMed ? medSearchRef : undefined} className="relative">
                                            <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-1">
                                                {isMed ? 'Nombre del medicamento' : isPeriodic ? 'Descripción' : 'Nombre del servicio / procedimiento'}
                                            </label>
                                            <div className="relative">
                                                {isMed && <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />}
                                                <input
                                                    type="text"
                                                    placeholder={isMed ? 'Buscar en POS o escribir nombre...' : isPeriodic ? 'Ej: Control de signos vitales' : 'Ej: Limpieza de herida quirúrgica'}
                                                    value={treatmentForm.name}
                                                    onChange={e => {
                                                        const val = e.target.value;
                                                        setTreatmentForm(prev => ({ ...prev, name: val, pos_product_id: '' }));
                                                        if (isMed) handleMedSearch(val);
                                                    }}
                                                    onFocus={() => isMed && treatmentForm.name.length >= 1 && setMedDropOpen(true)}
                                                    className={`w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl ${isMed ? 'pl-9' : 'px-3'} pr-3 py-2.5 text-xs font-bold text-slate-700 outline-none`}
                                                    required
                                                />
                                                {isMed && medSearchLoading && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-indigo-400 animate-spin" />}
                                            </div>
                                            {isMed && medDropOpen && (
                                                <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                                                    {medResults.length === 0 ? (
                                                        <p className="text-[10px] text-slate-400 font-bold px-4 py-3 italic">Sin resultados en POS — se guardará con el nombre ingresado</p>
                                                    ) : medResults.map(p => (
                                                        <button
                                                            key={p.id}
                                                            type="button"
                                                            onClick={() => selectMedProduct(p)}
                                                            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-indigo-50 transition-colors text-left"
                                                        >
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-xs font-black text-slate-800">{p.name}</p>
                                                                {p.description && (
                                                                    <p className="text-[9px] text-indigo-600 font-bold mt-0.5 leading-snug">{p.description}</p>
                                                                )}
                                                                <p className="text-[8px] text-slate-400 font-bold uppercase mt-0.5">{p.unit} · {p.sku || '—'}</p>
                                                            </div>
                                                            <div className="text-right shrink-0 ml-3">
                                                                <p className="text-xs font-black text-emerald-600">${p.price.toLocaleString('es-CL')}</p>
                                                                <p className={`text-[8px] font-black uppercase ${p.low_stock ? 'text-rose-500' : 'text-slate-400'}`}>
                                                                    Stock: {p.stock_quantity} {p.low_stock ? '⚠ Bajo' : ''}
                                                                </p>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                        </div>}

                                        {/* Periodic scheduling controls */}
                                        {isPeriodic && (
                                            isVitals ? (
                                                /* Vitales: solo hora inicio + intervalo, sin dosis */
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-1">Primera toma</label>
                                                        <input
                                                            type="time"
                                                            value={treatmentForm.start_time}
                                                            onChange={e => setTreatmentForm(prev => ({ ...prev, start_time: e.target.value }))}
                                                            className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2.5 text-xs font-bold text-slate-700 outline-none"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-1">Intervalo (horas)</label>
                                                        <input
                                                            type="number"
                                                            value={treatmentForm.frequency_hours}
                                                            onChange={e => setTreatmentForm(prev => ({ ...prev, frequency_hours: parseInt(e.target.value) || 1 }))}
                                                            className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2.5 text-xs font-bold text-slate-700 outline-none text-center"
                                                            min="1" max="72"
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-1">Hora inicio</label>
                                                        <input
                                                            type="time"
                                                            value={treatmentForm.start_time}
                                                            onChange={e => setTreatmentForm(prev => ({ ...prev, start_time: e.target.value }))}
                                                            className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2.5 text-xs font-bold text-slate-700 outline-none"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-1">Cada (horas)</label>
                                                        <input
                                                            type="number"
                                                            value={treatmentForm.frequency_hours}
                                                            onChange={e => setTreatmentForm(prev => ({ ...prev, frequency_hours: parseInt(e.target.value) || 8 }))}
                                                            className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2.5 text-xs font-bold text-slate-700 outline-none text-center"
                                                            min="1" max="72"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-1">Dosis (0=∞)</label>
                                                        <input
                                                            type="number"
                                                            value={treatmentForm.doses_count}
                                                            onChange={e => setTreatmentForm(prev => ({ ...prev, doses_count: parseInt(e.target.value) || 0 }))}
                                                            className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-2 py-2.5 text-xs font-bold text-slate-700 outline-none text-center"
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>
                                            )
                                        )}

                                        {/* Price */}
                                        {!isPeriodic || isMed ? (
                                            <div>
                                                <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block mb-1">
                                                    {isMed ? 'Precio por unidad (desde POS)' : 'Costo / Precio (opcional)'}
                                                </label>
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    value={treatmentForm.price || ''}
                                                    onChange={e => setTreatmentForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                                                    className="w-full bg-white border-2 border-slate-200 focus:border-indigo-400 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-700 outline-none"
                                                    readOnly={isMed && !!treatmentForm.pos_product_id}
                                                />
                                            </div>
                                        ) : null}

                                        <div className="flex gap-2 justify-end pt-1">
                                            <button
                                                type="button"
                                                onClick={() => { setShowAddTreatment(false); resetTreatmentForm(); }}
                                                className="px-4 py-2 border border-slate-200 text-slate-500 rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-slate-100"
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={addingTreatment || !treatmentForm.name.trim()}
                                                className="px-6 py-2 bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                {addingTreatment ? 'Planificando...' : 'Planificar'}
                                            </button>
                                        </div>
                                    </form>
                                    );
                                })()}

                                {/* Treatments Checklist */}
                                <div className="space-y-3">
                                    {selectedHosp.treatments.map((t: any) => {
                                        const meta = TASK_META[t.treatment_type as TreatmentType] || TASK_META.observation;
                                        const isPeriodic = (PERIODIC_TYPES as readonly string[]).includes(t.treatment_type);
                                        const isVitalsT = t.treatment_type === 'vitals';
                                        const execCount = selectedHosp.logs.filter((l: any) => l.treatment_id === t.id).length;
                                        const dosesTotal = parseInt(String(t.doses_count)) || 0;
                                        const dosesLeft = dosesTotal > 0 ? Math.max(0, dosesTotal - execCount) : null;
                                        const lastLog = selectedHosp.logs.find((l: any) => l.treatment_id === t.id);
                                        const isComplete = dosesTotal > 0 && execCount >= dosesTotal;
                                        const isEditingInterval = editIntervalId === t.id;

                                        return (
                                            <div key={t.id} className={`rounded-2xl border-2 overflow-hidden transition-all ${isComplete ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                                                <div className="p-4 flex items-start gap-3 relative">
                                                {/* Watermark checkmark para completados */}
                                                {isComplete && (
                                                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none select-none">
                                                        <CheckCircle2 size={72} className="text-emerald-200" strokeWidth={1.5} />
                                                    </div>
                                                )}

                                                {/* Icon */}
                                                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isComplete ? 'bg-emerald-100 text-emerald-600' : meta.color}`}>
                                                    {isComplete ? <Check size={18} /> : <meta.icon size={16} />}
                                                </div>

                                                <div className="flex-1 min-w-0 relative z-10">
                                                    <div className="flex items-start gap-2 flex-wrap">
                                                        <h5 className={`font-black text-xs uppercase flex-1 ${isComplete ? 'text-emerald-700' : 'text-slate-800'}`}>{t.name}</h5>
                                                    </div>

                                                    {/* Schedule badges para periódicas */}
                                                    {isPeriodic && (
                                                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                                            {t.start_time && (
                                                                <span className="text-[8px] font-black bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full uppercase flex items-center gap-1">
                                                                    <Clock size={8} /> Inicio {t.start_time}
                                                                </span>
                                                            )}
                                                            {parseInt(String(t.frequency_hours)) > 0 && (
                                                                <span className="text-[8px] font-black bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full uppercase">
                                                                    Cada {t.frequency_hours}h
                                                                </span>
                                                            )}
                                                            {dosesTotal === 0 && execCount > 0 && (
                                                                <span className="text-[8px] font-black bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full uppercase">
                                                                    {execCount} ejecución{execCount !== 1 ? 'es' : ''}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Barra de progreso de dosis */}
                                                    {isPeriodic && dosesTotal > 0 && (
                                                        <div className="mt-2 space-y-1">
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-[9px] font-black text-slate-400 uppercase">Dosis</span>
                                                                <span className={`text-[9px] font-black uppercase ${isComplete ? 'text-emerald-600' : 'text-slate-600'}`}>
                                                                    {execCount} / {dosesTotal} {isComplete ? '· Completado ✓' : `· ${dosesLeft} restante${dosesLeft !== 1 ? 's' : ''}`}
                                                                </span>
                                                            </div>
                                                            <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-500' : 'bg-indigo-500'}`}
                                                                    style={{ width: `${Math.min(100, (execCount / dosesTotal) * 100)}%` }}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {lastLog ? (
                                                        <p className="text-[9px] text-slate-400 font-bold mt-1.5 flex items-center gap-1">
                                                            <Check size={9} className="text-emerald-500 shrink-0" />
                                                            Último: {lastLog.details.length > 35 ? lastLog.details.substring(0,35) + '…' : lastLog.details}
                                                            <span className="text-slate-300">·</span>
                                                            {new Date(lastLog.logged_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    ) : (
                                                        <p className="text-[9px] text-slate-400 font-bold mt-1.5 italic">Sin registros aún</p>
                                                    )}
                                                </div>

                                                {selectedHosp.status === 'admitted' && !isComplete && isPeriodic && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedTreatment(t);
                                                            if (t.treatment_type === 'vitals') {
                                                                setLogForm((prev: any) => ({ ...prev, action_type: 'vitals_check' }));
                                                            } else {
                                                                setLogForm((prev: any) => ({ ...prev, action_type: 'treatment_done', details: 'Administrado' }));
                                                            }
                                                            setShowLogModal(true);
                                                        }}
                                                        className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black uppercase text-[9px] tracking-widest shrink-0 transition-transform active:scale-95"
                                                    >
                                                        Registrar
                                                    </button>
                                                )}
                                                {!isPeriodic && execCount > 0 && (
                                                    <span className="flex items-center gap-1 text-[8px] font-black text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-xl shrink-0">
                                                        <Check size={9} /> Cobrado
                                                    </span>
                                                )}
                                                {!isPeriodic && execCount === 0 && (
                                                    <span className="flex items-center gap-1 text-[8px] font-black text-amber-600 bg-amber-50 border border-amber-100 px-2 py-1 rounded-xl shrink-0">
                                                        Pendiente
                                                    </span>
                                                )}
                                                </div>

                                            {/* Cambiar intervalo — solo para vitales */}
                                            {isVitalsT && selectedHosp.status === 'admitted' && (
                                                <div className="border-t border-slate-100 px-4 py-2.5">
                                                    {isEditingInterval ? (
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-[9px] font-black text-slate-400 uppercase shrink-0">Nuevo intervalo:</span>
                                                            <input
                                                                type="number"
                                                                value={editIntervalHours}
                                                                onChange={e => setEditIntervalHours(parseInt(e.target.value) || 1)}
                                                                className="w-16 bg-white border-2 border-indigo-300 focus:border-indigo-500 rounded-xl px-2 py-1 text-xs font-black text-center outline-none"
                                                                min="1" max="72"
                                                                autoFocus
                                                            />
                                                            <span className="text-[9px] text-slate-400 font-bold">horas</span>
                                                            <button
                                                                onClick={() => handleUpdateInterval(t.id, editIntervalHours)}
                                                                disabled={updatingInterval}
                                                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black uppercase text-[9px] tracking-wide transition-all disabled:opacity-50"
                                                            >
                                                                {updatingInterval ? 'Guardando...' : 'Guardar'}
                                                            </button>
                                                            <button
                                                                onClick={() => setEditIntervalId(null)}
                                                                className="px-2 py-1.5 text-slate-400 hover:text-slate-600 font-black text-[9px] uppercase transition-colors"
                                                            >
                                                                Cancelar
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => { setEditIntervalId(t.id); setEditIntervalHours(parseInt(String(t.frequency_hours)) || 1); }}
                                                            className="text-[10px] font-black text-indigo-500 hover:text-indigo-700 uppercase tracking-wide flex items-center gap-1.5 transition-colors"
                                                        >
                                                            <Clock size={11} /> Cambiar intervalo · actual: cada {t.frequency_hours}h
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        );
                                    })}

                                    {selectedHosp.treatments.length === 0 && (
                                        <p className="text-xs text-slate-400 italic text-center py-6">
                                            No se han planificado tareas aún.
                                        </p>
                                    )}
                                </div>

                            </div>
                        )}

                        {/* TAB 2: BITÁCORA CLINICA (LOGS) */}
                        {detailTab === 'logs' && (
                            <div className="space-y-6">
                                {/* Add quick note form */}
                                {selectedHosp.status === 'admitted' && (
                                    <form onSubmit={handleLogAction} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Registrar Nota Clínica / Vitals</h4>
                                        <div className="flex gap-2 mb-2">
                                            <button 
                                                type="button" 
                                                onClick={() => setLogForm(f => ({ ...f, action_type: 'note' }))}
                                                className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${logForm.action_type === 'note' ? 'bg-indigo-600 text-white' : 'bg-white border text-slate-400'}`}
                                            >
                                                Nota / Observación
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={() => setLogForm(f => ({ ...f, action_type: 'vitals_check' }))}
                                                className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase transition-all ${logForm.action_type === 'vitals_check' ? 'bg-indigo-600 text-white' : 'bg-white border text-slate-400'}`}
                                            >
                                                Signos Vitales
                                            </button>
                                        </div>

                                        {logForm.action_type === 'vitals_check' ? (
                                            <div className="space-y-2 animate-in fade-in">
                                                <div className="grid grid-cols-3 gap-2">
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block">T° (°C)</label>
                                                        <input type="text" placeholder="38.5" value={logForm.vitals.temp} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, temp: e.target.value } })} className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2 text-xs font-bold text-center outline-none" />
                                                    </div>
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block">FC (lpm)</label>
                                                        <input type="text" placeholder="120" value={logForm.vitals.fc} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, fc: e.target.value } })} className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2 text-xs font-bold text-center outline-none" />
                                                    </div>
                                                    <div>
                                                        <label className="text-[8px] font-black text-slate-400 uppercase ml-1 block">FR (rpm)</label>
                                                        <input type="text" placeholder="30" value={logForm.vitals.fr} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, fr: e.target.value } })} className="w-full bg-white border border-slate-200 rounded-xl px-2 py-2 text-xs font-bold text-center outline-none" />
                                                    </div>
                                                </div>
                                                <input type="text" placeholder="Comentarios adicionales..." value={logForm.vitals.comment} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, comment: e.target.value } })} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none" />
                                            </div>
                                        ) : (
                                            <textarea 
                                                placeholder="Escribe la observación clínica de la mascota..."
                                                value={logForm.details}
                                                onChange={e => setLogForm({ ...logForm, details: e.target.value })}
                                                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs font-bold outline-none resize-none"
                                                rows={2}
                                            />
                                        )}

                                        <div className="flex gap-2">
                                            <input 
                                                type="text" 
                                                placeholder="Veterinario"
                                                value={logForm.staff_name}
                                                onChange={e => setLogForm({ ...logForm, staff_name: e.target.value })}
                                                className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-[10px] font-bold text-slate-600 outline-none w-28 shrink-0"
                                            />
                                            <button 
                                                type="submit" 
                                                disabled={logging || (logForm.action_type === 'note' && !logForm.details.trim())}
                                                className="flex-1 py-2 bg-indigo-600 text-white rounded-xl text-[9px] font-black uppercase tracking-wider hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                {logging ? 'Guardando...' : 'Guardar en Bitácora'}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {/* Logs list */}
                                <div className="space-y-4">
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Línea de Tiempo de Controles</h4>
                                    <div className="space-y-4 border-l border-slate-100 pl-4 ml-2">
                                        {selectedHosp.logs.map((l) => (
                                            <div key={l.id} className="relative space-y-1">
                                                {/* Bullet */}
                                                <span className={`absolute -left-[21px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                                                    l.action_type === 'status_change' ? 'bg-indigo-600' : l.action_type === 'vitals_check' ? 'bg-sky-500' : 'bg-slate-400'
                                                }`} />

                                                <div className="flex justify-between items-center text-[10px] text-slate-400 font-bold">
                                                    <span>
                                                        {new Date(l.logged_at).toLocaleDateString('es-CL', {
                                                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                                        })}
                                                    </span>
                                                    <span className="uppercase text-[9px] text-slate-500 font-black">
                                                        Por: {l.staff_name}
                                                    </span>
                                                </div>

                                                <p className="text-xs text-slate-700 font-medium leading-relaxed">
                                                    {l.treatment_name && <strong className="text-indigo-600 uppercase tracking-tight mr-1">[{l.treatment_name}]</strong>}
                                                    {l.details}
                                                </p>

                                                {l.billed === 1 && (
                                                    <span className="inline-block text-[8px] font-black bg-emerald-50 text-emerald-600 px-1.5 py-0.5 rounded uppercase tracking-wider mt-1 border border-emerald-100">
                                                        Facturado ✓
                                                    </span>
                                                )}
                                            </div>
                                        ))}

                                        {selectedHosp.logs.length === 0 && (
                                            <p className="text-xs text-slate-400 italic">
                                                Sin actividad registrada.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* TAB 3: COBROS — carrito de facturación */}
                        {detailTab === 'billing' && (() => {
                            const grouped: Record<string, { name: string; type: string; price: number; qty: number; billedQty: number }> = {};
                            selectedHosp.treatments.forEach((t: any) => {
                                const key = t.name.toLowerCase().trim();
                                const price = parseFloat(String(t.price)) || 0;
                                const logsForT = selectedHosp.logs.filter((l: any) => l.treatment_id === t.id);
                                const qty = logsForT.length;
                                const billedQty = logsForT.filter((l: any) => l.billed === 1).length;
                                if (qty === 0) return;
                                if (grouped[key]) { grouped[key].qty += qty; grouped[key].billedQty += billedQty; }
                                else { grouped[key] = { name: t.name, type: t.treatment_type, price, qty, billedQty }; }
                            });
                            const items = Object.values(grouped);
                            const total = items.reduce((s, i) => s + i.price * i.qty, 0);
                            const pendingTotal = items.reduce((s, i) => s + i.price * Math.max(0, i.qty - i.billedQty), 0);
                            const allBilled = items.every(i => i.billedQty >= i.qty);

                            return (
                                <div className="space-y-0 -mx-6">
                                    {/* Header de tabla */}
                                    <div className="flex items-center justify-between px-6 pb-3">
                                        <h4 className="text-xs font-black text-slate-500 uppercase tracking-widest flex items-center gap-2">
                                            <ShoppingCart size={13} /> Resumen de Cobros
                                        </h4>
                                        {items.length > 0 && !allBilled && selectedHosp.status === 'admitted' && (
                                            <button onClick={handleSendToPOS} className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-black uppercase text-[9px] tracking-widest shadow-md transition-all active:scale-95 flex items-center gap-1.5">
                                                <DollarSign size={11} /> Cobrar POS
                                            </button>
                                        )}
                                    </div>

                                    {items.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-14 text-slate-400 text-center px-6">
                                            <ShoppingCart size={36} className="mb-3 opacity-20" />
                                            <p className="text-xs font-black uppercase italic">Sin cobros registrados</p>
                                            <p className="text-[10px] font-bold mt-1">Agrega tareas o servicios desde la pestaña Tareas</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* Cabecera de columnas */}
                                            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-4 px-6 py-2 bg-slate-50 border-y border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                                <span>Ítem</span>
                                                <span className="text-center w-10">Cant.</span>
                                                <span className="text-right w-20">P. Unit.</span>
                                                <span className="text-right w-20">Total</span>
                                            </div>

                                            {/* Filas */}
                                            {items.map((item, idx) => {
                                                const meta = TASK_META[item.type as TreatmentType] || TASK_META.observation;
                                                const subtotal = item.price * item.qty;
                                                const isBilled = item.billedQty >= item.qty;
                                                return (
                                                    <div key={idx} className={`grid grid-cols-[1fr_auto_auto_auto] gap-4 items-center px-6 py-4 border-b border-slate-100 ${isBilled ? 'bg-emerald-50/40' : 'bg-white'}`}>
                                                        {/* Nombre */}
                                                        <div className="flex items-center gap-3 min-w-0">
                                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}>
                                                                <meta.icon size={13} />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="font-black text-slate-800 text-sm uppercase truncate">{item.name}</p>
                                                                {isBilled
                                                                    ? <span className="text-[9px] font-black text-emerald-600 flex items-center gap-0.5"><Check size={9} /> Facturado</span>
                                                                    : <span className="text-[9px] font-black text-amber-500">Pendiente</span>
                                                                }
                                                            </div>
                                                        </div>
                                                        {/* Cantidad */}
                                                        <span className="text-center w-10 text-lg font-black text-slate-500 tabular-nums">{item.qty}</span>
                                                        {/* Precio unit */}
                                                        <span className="text-right w-20 text-sm font-bold text-slate-400 tabular-nums">
                                                            {item.price > 0 ? `$${item.price.toLocaleString('es-CL')}` : '—'}
                                                        </span>
                                                        {/* Subtotal */}
                                                        <div className="text-right w-20">
                                                            <p className="text-base font-black text-slate-800 tabular-nums">
                                                                {item.price > 0 ? `$${subtotal.toLocaleString('es-CL')}` : '—'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Total */}
                                            <div className="flex items-center justify-between bg-slate-800 text-white px-6 py-4 mt-2 mx-6 rounded-2xl">
                                                <span className="text-sm font-black uppercase tracking-wide">Total Pendiente</span>
                                                <span className="text-2xl font-black tabular-nums">${pendingTotal.toLocaleString('es-CL')}</span>
                                            </div>
                                            {pendingTotal < total && (
                                                <p className="text-[10px] text-emerald-600 font-bold text-right px-6 pt-2">
                                                    Ya facturado: ${(total - pendingTotal).toLocaleString('es-CL')}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        })()}

                        {/* TAB 4: CLINICAL CALCULATOR */}
                        {detailTab === 'calculators' && (
                            <div className="space-y-6">
                                <div className="bg-slate-50 border border-slate-200 rounded-[2rem] p-6 space-y-4">
                                    <h4 className="text-sm font-black uppercase italic text-slate-800 tracking-tight flex items-center gap-2">
                                        <Calculator className="text-indigo-500" size={16} /> Calculadora CRI y Fluidos
                                    </h4>
                                    <p className="text-[10px] text-slate-400 font-medium">
                                        Calcula goteo de infusión constante y agregados de fármacos a la bolsa del suero de forma parametrizada.
                                    </p>

                                    <div className="space-y-3 pt-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">Peso Paciente (Kg)</label>
                                                <input type="number" placeholder="Ej: 12" value={cri.weight} onChange={e => setCri({ ...cri, weight: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-indigo-500" />
                                            </div>
                                            <div>
                                                <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">Tasa Fluido (ml/kg/h)</label>
                                                <input type="number" placeholder="Ej: 4" value={cri.fluidRate} onChange={e => setCri({ ...cri, fluidRate: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-indigo-500" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">Volumen Bolsa (ml)</label>
                                                <input type="number" placeholder="500" value={cri.bagVolume} onChange={e => setCri({ ...cri, bagVolume: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-indigo-500" />
                                            </div>
                                            <div>
                                                <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">Dosis Fármaco</label>
                                                <div className="flex gap-1">
                                                    <input type="number" placeholder="Ej: 0.1" value={cri.dose} onChange={e => setCri({ ...cri, dose: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-indigo-500" />
                                                    <select value={cri.doseUnit} onChange={e => setCri({ ...cri, doseUnit: e.target.value })} className="bg-white border border-slate-200 rounded-xl text-[9px] font-black uppercase outline-none px-1">
                                                        <option value="hr">/kg/h</option>
                                                        <option value="day">/kg/d</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">Concentración Fármaco (mg/ml)</label>
                                            <input type="number" placeholder="Ej: 10" value={cri.concentration} onChange={e => setCri({ ...cri, concentration: e.target.value })} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:border-indigo-500" />
                                        </div>
                                    </div>

                                    {criCalculations ? (
                                        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 pt-4 mt-4 animate-in fade-in">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none border-b pb-2">Resultados Tasa e Infusión</p>
                                            <div className="grid grid-cols-2 gap-3 text-xs">
                                                <div>
                                                    <span className="block text-[8px] font-black text-slate-400 uppercase">Tasa Total</span>
                                                    <strong className="text-slate-800 text-sm">{criCalculations.mlPerHour} ml/hr</strong>
                                                </div>
                                                <div>
                                                    <span className="block text-[8px] font-black text-slate-400 uppercase">Duración Bolsa</span>
                                                    <strong className="text-slate-800 text-sm">{criCalculations.duration} horas</strong>
                                                </div>
                                                <div>
                                                    <span className="block text-[8px] font-black text-slate-400 uppercase">Goteo (20g/ml)</span>
                                                    <strong className="text-slate-800 text-sm">{criCalculations.dropsMin} gotas/min</strong>
                                                </div>
                                                {criCalculations.drugVolume > 0 && (
                                                    <div>
                                                        <span className="block text-[8px] font-black text-emerald-600 uppercase">Añadir a la bolsa</span>
                                                        <strong className="text-emerald-600 text-sm">{criCalculations.drugVolume} ml</strong>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center text-[10px] text-slate-400 italic pt-4">
                                            Completa los campos Peso, Tasa y Volumen Bolsa para ver los cálculos.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            ) : null}
            </div>
            )}

            {/* ADMIT PATIENT MODAL */}
            {showAdmitModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-[3rem] w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in">
                        <div className="bg-indigo-600 p-8 text-white relative">
                            <button onClick={() => setShowAdmitModal(false)} className="absolute top-6 right-6 p-2 hover:bg-white/10 rounded-full transition-colors"><X size={24} /></button>
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-2xl">
                                    <Bed size={32} />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-black uppercase italic tracking-tighter">Hospitalizar Paciente</h3>
                                    <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest">Registrar ingreso a internado</p>
                                </div>
                            </div>
                        </div>
                        <form onSubmit={handleAdmit} className="p-8 space-y-6">
                            
                            {/* Pet Dropdown */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Seleccionar Paciente *</label>
                                <select 
                                    value={hospForm.pet_id}
                                    onChange={e => setHospForm({ ...hospForm, pet_id: e.target.value })}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 font-bold text-slate-700 outline-none focus:border-indigo-500 cursor-pointer"
                                    required
                                >
                                    <option value="">-- Elige un paciente registrado --</option>
                                    {linkedPatients.map(p => (
                                        <option key={p.id} value={p.id}>{p.name} ({p.breed || 'Mestizo'} · Propietario: {p.ownerName || 'Sin dueño'})</option>
                                    ))}
                                </select>
                            </div>

                            {/* Cage Label */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nombre/Etiqueta Jaula *</label>
                                <input 
                                    type="text" 
                                    placeholder="Ej: Jaula Felinos 02, Canil 05"
                                    value={hospForm.cage_label}
                                    onChange={e => setHospForm({ ...hospForm, cage_label: e.target.value })}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 font-bold text-slate-700 outline-none focus:border-indigo-500"
                                    required
                                />
                            </div>

                            {/* Gravity & Reason */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Gravedad Inicial</label>
                                    <select 
                                        value={hospForm.gravity}
                                        onChange={e => setHospForm({ ...hospForm, gravity: e.target.value as any })}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 font-bold text-slate-700 outline-none focus:border-indigo-500"
                                    >
                                        <option value="low">Estable (Verde)</option>
                                        <option value="medium">Estable s Delicado (Amarillo)</option>
                                        <option value="critical">Crítico (Rojo)</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Diagnóstico / Motivo *</label>
                                    <input 
                                        type="text" 
                                        placeholder="Ej: Post-op esterilización, Parvovirus"
                                        value={hospForm.reason}
                                        onChange={e => setHospForm({ ...hospForm, reason: e.target.value })}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 font-bold text-slate-700 outline-none focus:border-indigo-500"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Initial Notes */}
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Notas Iniciales</label>
                                <textarea 
                                    placeholder="Indicaciones y notas iniciales..."
                                    value={hospForm.initial_notes}
                                    onChange={e => setHospForm({ ...hospForm, initial_notes: e.target.value })}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 text-xs font-bold outline-none focus:border-indigo-500 resize-none"
                                    rows={3}
                                />
                            </div>

                            <button 
                                type="submit"
                                disabled={admitting}
                                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-5 rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-xl flex items-center justify-center gap-3 transition-all active:scale-[0.98] disabled:opacity-50"
                            >
                                {admitting ? <Loader2 className="animate-spin" /> : <CheckCircle2 size={20} />}
                                {admitting ? 'Procesando Ingreso...' : 'Confirmar Hospitalización'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* FLOW ACTION LOG MODAL */}
            {showLogModal && selectedTreatment && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-[3rem] w-full max-w-md shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in">
                        <div className="bg-indigo-600 p-8 text-white relative">
                            <button onClick={() => { setShowLogModal(false); setSelectedTreatment(null); }} className="absolute top-6 right-6 p-2 hover:bg-white/10 rounded-full transition-colors"><X size={24} /></button>
                            <div className="flex items-center gap-4">
                                <CheckCircle2 size={32} />
                                <div>
                                    <h3 className="text-xl font-black uppercase italic tracking-tighter">Registrar Ejecución</h3>
                                    <p className="text-[9px] font-bold text-white/70 uppercase tracking-widest">Tarea: {selectedTreatment.name}</p>
                                </div>
                            </div>
                        </div>
                        <form onSubmit={handleLogAction} className="p-8 space-y-6">
                            {selectedTreatment.treatment_type === 'vitals' ? (
                                <div className="space-y-4">
                                    <p className="text-xs text-slate-500 font-bold uppercase">Ingresa los signos vitales obtenidos:</p>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">T° (°C)</label>
                                            <input type="text" placeholder="38.5" value={logForm.vitals.temp} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, temp: e.target.value } })} className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-2 py-3 text-xs font-bold text-center outline-none" required />
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">FC (lpm)</label>
                                            <input type="text" placeholder="120" value={logForm.vitals.fc} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, fc: e.target.value } })} className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-2 py-3 text-xs font-bold text-center outline-none" required />
                                        </div>
                                        <div>
                                            <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">FR (rpm)</label>
                                            <input type="text" placeholder="30" value={logForm.vitals.fr} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, fr: e.target.value } })} className="w-full bg-slate-50 border-2 border-slate-100 rounded-xl px-2 py-3 text-xs font-bold text-center outline-none" required />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">Observación</label>
                                        <input type="text" placeholder="Comentarios clínicos..." value={logForm.vitals.comment} onChange={e => setLogForm({ ...logForm, vitals: { ...logForm.vitals, comment: e.target.value } })} className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3.5 text-xs font-bold outline-none" />
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Observaciones / Detalles</label>
                                    <textarea 
                                        placeholder="Ej: Administrado vía endovenosa lenta. Paciente reacciona bien."
                                        value={logForm.details}
                                        onChange={e => setLogForm({ ...logForm, details: e.target.value })}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 text-xs font-bold outline-none focus:border-indigo-500 resize-none"
                                        rows={3}
                                        required
                                    />
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[9px] font-black text-slate-400 uppercase ml-1 block">Firma Personal</label>
                                    <input 
                                        type="text" 
                                        value={logForm.staff_name}
                                        onChange={e => setLogForm({ ...logForm, staff_name: e.target.value })}
                                        className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-4 py-3 font-bold text-slate-700 outline-none text-xs"
                                        required
                                    />
                                </div>
                                <div className="flex items-end">
                                    <button 
                                        type="submit"
                                        disabled={logging}
                                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-md flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50"
                                    >
                                        {logging ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle2 size={12} />}
                                        Guardar
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* DISCHARGE MODAL */}
            {showDischargeModal && selectedHosp && (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[110] flex items-center justify-center p-4">
                    <div className="bg-white rounded-[3rem] w-full max-w-md shadow-2xl border border-slate-100 overflow-hidden animate-in zoom-in">
                        <div className="bg-rose-600 p-8 text-white relative">
                            <button
                                onClick={() => { setShowDischargeModal(false); setDischargeNotes(''); }}
                                className="absolute top-6 right-6 p-2 hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X size={24} />
                            </button>
                            <div className="flex items-center gap-4">
                                <CheckCircle2 size={32} />
                                <div>
                                    <h3 className="text-xl font-black uppercase italic tracking-tighter">Alta Médica</h3>
                                    <p className="text-[9px] font-bold text-white/70 uppercase tracking-widest">{selectedHosp.pet_name} — {selectedHosp.cage_label}</p>
                                </div>
                            </div>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Observaciones de Alta (opcional)</label>
                                <textarea
                                    placeholder="Indicaciones post-hospitalización, cuidados en casa, medicación a continuar..."
                                    value={dischargeNotes}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDischargeNotes(e.target.value)}
                                    className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-6 py-4 text-xs font-bold outline-none focus:border-rose-400 resize-none"
                                    rows={4}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => { setShowDischargeModal(false); setDischargeNotes(''); }}
                                    className="py-4 border-2 border-slate-200 rounded-2xl font-black uppercase text-[10px] tracking-widest text-slate-500 hover:bg-slate-50 transition-all"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleDischarge}
                                    disabled={discharging}
                                    className="py-4 bg-rose-600 hover:bg-rose-700 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-lg flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-50"
                                >
                                    {discharging ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                                    {discharging ? 'Procesando…' : 'Confirmar Alta'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};
